import { CSS } from '@react-spring/web';
import { SystemProp, Theme } from '@xstyled/styled-components';
import { Breakpoints } from '~/lib/useResponsive';
import {
  AUDIO_POPUP,
  CARD,
  CardDefinition,
  IMAGE,
  ImageDefinition,
  ItemIds,
  LayoutListDefinition,
  LAYOUT_LIST,
  TEXT,
  Alignment,
  VideoDefinition,
  VIDEO,
  CLOSE_CARD
} from '../../items';
import { AudioPopupDefinition } from '../../items/audio-popup/AudioPopup';
import { ItemDefinition } from '../../items/Item';
import { ItemHelper } from './types';
import { isIOS, isSafari } from 'react-device-detect';

/**
 * Turns an API Card item into a tree of items with a CARD item as root
 * @param src The API Card item
 * @returns A CARD item with child items
 */
export const CardHelper: ItemHelper<
  'Card' | 'Question',
  CardDefinition,
  {
    width?: CardDefinition['values']['width'];
    height?: CardDefinition['values']['height'];
    padding?: CardDefinition['values']['padding'];
    extraItems?: ItemDefinition<ItemIds>[];
    align?: Alignment;
    clearStyles?: Breakpoints;
    blocking?: boolean;
    replay?: boolean;
    canScroll?: boolean;
    className?: string;
    isImageDecorative?: boolean;
    type?: string;
    extraType?: string;
    minHeight?: string;
    doorId?: string;
    centerTitle?: boolean;
    canWeDeleteCard?: boolean;
    editable?: boolean;
    overflow?: SystemProp<CSS.Property.Overflow, Theme>;
  }
> = (src, options) => {
  let rootItem: LayoutListDefinition | null = null;
  let videos: VideoDefinition[] = [];

  if (
    (src.__typename === 'Card' || src.__typename === 'Question') &&
    src.videos?.length
  ) {
    videos = src.videos
      .map(v => {
        const mediaUrl =
          isIOS || isSafari
            ? ([v.mediaUrl && { src: v.mediaUrl, type: 'video/mp4' }].filter(
                Boolean
              ) as Array<{ src: string; type: string }>)
            : ([
                v.hlsUrl && {
                  src: v.hlsUrl,
                  type: 'application/vnd.apple.mpegurl'
                },
                v.mediaUrl && { src: v.mediaUrl, type: 'video/mp4' }
              ].filter(Boolean) as Array<{ src: string; type: string }>);

        if (!mediaUrl || mediaUrl?.length === 0) {
          return null;
        }

        return {
          type: VIDEO,
          id: v.id,
          values: {
            videoId: v?.id,
            mediaUrl: mediaUrl,
            autoPlay: v.autoPlay,
            subtitlesUrl: v.subtitlesUrl,
            snapshotUrl: v.snapshotUrl,
            showPlayButton: true,
            isolated: true,
            blocking: options?.blocking,
            loop: v.loop || false,
            replay:
              typeof options?.replay !== undefined ? options?.replay : true,
            videoContainerStyles: {
              width: '100%',
              height: '233px',
              position: 'relative'
            },
            videoRecord: v.videoRecord
          }
        } as VideoDefinition;
      })
      .filter(Boolean) as VideoDefinition[];
  }

  let pictures: ImageDefinition[] = [];
  if (src.pictures?.length) {
    let replay = false;

    if (options && options.replay) {
      replay = true;
    }

    /* in the Interactive30 template, we need to implement replay for audio content */
    /* in the Cards5 template, we need to implement replay for audio content */
    try {
      throw new Error();
    } catch (error) {
      if (error.stack.includes('Interactive30')) {
        replay = true;
      }
    }

    pictures = src.pictures.map(
      (p, i) =>
        ({
          id: p.id,
          type: IMAGE,
          values: {
            url: p.mediaUrl,
            alt: options?.isImageDecorative ? '' : p.name,
            ...(options?.editable === false ? { editable: false } : {})
          },
          items: [
            src.audios?.[i] &&
              ({
                type: AUDIO_POPUP,
                id: src.audios[i].id,
                values: {
                  mediaUrl: src.audios[i].mediaUrl,
                  subtitlesUrl: src.audios[i].subtitlesUrl,
                  autoPlay: src.audios[i].autoPlay,
                  name: src.audios[i].name,
                  blocking: src.__typename === 'Card',
                  replay: replay,
                  parentId: src.id,
                  generated: src.audios?.[i]?.generated || false,
                  ...(options?.editable === false ? { editable: false } : {})
                }
              } as AudioPopupDefinition)
          ].filter(Boolean)
        }) as ImageDefinition
    );
  }
  if (pictures.length || videos.length) {
    rootItem = {
      id: `${src.id}.list`,
      type: LAYOUT_LIST,
      values: {},
      items: [...pictures, ...videos]
    };
  }
  const isDoorsCard = options?.className === 'doorsCard';
  const textList: LayoutListDefinition = {
    id: `${src.id}.text-list`,
    type: LAYOUT_LIST,
    values: {
      padded: true,
      type:
        (options && options.extraType) || (options && options.type) || 'div',
      align: (options && options.align) || 'center',
      overflow: (options && options.overflow) || 'hidden'
      // align: isDoorsCard ? 'left' : (options && options.align) || 'center'
    },
    items: []
  };

  if (src.subtitle) {
    textList.items.push({
      id: `${src.id}_subtitle`,
      type: TEXT,
      values: {
        value: src.subtitle,
        variant: 'sub_heading',
        icon: src?.icon,
        ...(options?.editable === false ? { editable: false } : {})
      },
      items: []
    });
  }

  if (src.title) {
    textList.items.push({
      id: `${src.id}_title`,
      type: TEXT,
      values: {
        value: src.title,
        variant: 'heading3',
        center: options?.centerTitle,
        ...(options?.editable === false ? { editable: false } : {})
      },
      items: []
    });
  }

  if (src.body) {
    textList.items.push({
      id: `${src.id}_body`,
      type: TEXT,
      values: {
        value: src.body,
        canScroll: isDoorsCard ? true : options && options.canScroll,
        padding: isDoorsCard ? '11px' : undefined,
        fontSize: isDoorsCard ? '16px' : undefined,
        maxHeight: isDoorsCard ? '100%' : undefined,
        ...(options?.editable === false ? { editable: false } : {})
      },
      items: []
    });
  }

  if (options?.extraItems?.length) {
    textList.items = [...textList.items, ...options.extraItems];
  }

  if (rootItem && textList.items.length) {
    rootItem.items.push(textList);
  }

  const closeCardObject = {
    id: `${src.id}.edit-button2`,
    type: CLOSE_CARD,
    values: {
      isSubmit: true,
      text: 'Got it!',
      isBlocked: true,
      cardId: options?.doorId,
      linkedAudioId: undefined
    },
    items: []
  } as ItemDefinition<ItemIds>;

  if (isDoorsCard) {
    if (rootItem) {
      (closeCardObject.values as Record<string, unknown>).linkedAudioId =
        src.audios?.[0]?.id;
      rootItem.items.push(closeCardObject);
    } else {
      textList.items.push(closeCardObject);
    }
  }

  return {
    id: src.id,
    position: src.position || undefined,
    start: src.appear || undefined,
    type: CARD,
    values: options || {},
    items: [rootItem || textList]
  };
};
